<template>
    <article
        class="changelog-entry"
        :class="[
            `changelog-entry--${variant}`,
            {
                'changelog-entry--first': isFirst,
                'changelog-entry--last': isLast,
            },
        ]"
    >
        <div class="changelog-entry__axis">
            <span class="changelog-entry__dot" />
        </div>
        <div class="changelog-entry__body">
            <div class="changelog-entry__meta">
                <p class="changelog-entry__date-version">
                    <span v-if="formattedDate" class="changelog-entry__date">{{ formattedDate }}</span>
                    <span v-if="entry.version" class="changelog-entry__version">{{ entry.version }}</span>
                </p>
                <span v-if="typeLabel" class="changelog-entry__type">{{ typeLabel }}</span>
            </div>
            <div class="changelog-entry__card">
                <div class="changelog-entry__content">
                    <h3 class="changelog-entry__title">
                        {{ entry.title }}
                    </h3>
                    <p v-if="entry.description" class="changelog-entry__description">
                        {{ entry.description }}
                    </p>
                </div>
                <ul v-if="entry.items && entry.items.length" class="changelog-entry__items">
                    <li v-for="(item, index) in entry.items" :key="index">
                        {{ item }}
                    </li>
                </ul>
            </div>
        </div>
    </article>
</template>

<script>
    import { formatReleasedAt } from '@/utils/changelogStorage';

    const TYPE_LABELS = {
        feature: 'Funkcja',
        improvement: 'Aktualizacja',
        fix: 'Poprawka',
        security: 'Bezpieczeństwo',
    };

    export default {
        name: 'ChangelogEntryItem',
        props: {
            entry: {
                type: Object,
                required: true,
            },
            variant: {
                type: String,
                default: 'dark',
            },
            isFirst: {
                type: Boolean,
                default: false,
            },
            isLast: {
                type: Boolean,
                default: false,
            },
        },
        computed: {
            typeLabel() {
                return TYPE_LABELS[this.entry.type] || '';
            },
            formattedDate() {
                return formatReleasedAt(this.entry.releasedAt);
            },
        },
    };
</script>

<style scoped>
    .changelog-entry {
        display: flex;
        gap: 8px;
        align-items: stretch;
    }

    .changelog-entry__axis {
        position: relative;
        width: 8px;
        flex-shrink: 0;
        align-self: stretch;
    }

    .changelog-entry__axis::before {
        content: '';
        position: absolute;
        top: 0;
        bottom: 0;
        left: 3px;
        width: 0;
        border-right: 1px solid #60737d;
    }

    .changelog-entry--first .changelog-entry__axis::before {
        top: 28px;
    }

    .changelog-entry--last .changelog-entry__axis::before {
        bottom: 16px;
    }

    .changelog-entry__dot {
        position: absolute;
        top: 28px;
        left: 0;
        z-index: 1;
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: var(--color-light-sage);
    }

    .changelog-entry__body {
        display: flex;
        flex: 1;
        min-width: 0;
        gap: 8px;
    }

    .changelog-entry__meta {
        display: flex;
        flex-direction: column;
        gap: 8px;
        width: 140px;
        flex-shrink: 0;
        padding: 24px 0;
    }

    .changelog-entry__date-version {
        margin: 0;
        font-size: 14px;
        font-weight: 600;
        line-height: 16px;
        color: var(--color-silver);
    }

    .changelog-entry__date {
        display: block;
        font-weight: 700;
        color: var(--color-white);
    }

    .changelog-entry__version {
        display: block;
        font-weight: 400;
    }

    .changelog-entry__type {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: fit-content;
        padding: 1px 4px 3px;
        border: 1px solid var(--color-slate-two);
        border-radius: 4px;
        font-size: 12px;
        line-height: 14px;
        color: var(--color-silver);
    }

    .changelog-entry__card {
        flex: 1;
        min-width: 0;
        display: flex;
        flex-direction: column;
        gap: 16px;
        padding: 24px;
        background: var(--color-slate);
        border-bottom: 1px solid var(--color-charcoal-grey);
    }

    .changelog-entry--first .changelog-entry__card {
        border-radius: 4px 4px 0 0;
    }

    .changelog-entry__content {
        display: flex;
        flex-direction: column;
        gap: 8px;
    }

    .changelog-entry__title {
        margin: 0;
        font-size: 18px;
        font-weight: 700;
        line-height: 26px;
        color: var(--color-silver);
    }

    .changelog-entry__description {
        margin: 0;
        font-size: 14px;
        line-height: 20px;
        color: var(--color-silver);
    }

    .changelog-entry__items {
        margin: 0;
        padding: 0 0 0 8px;
        list-style: none;
        display: flex;
        flex-direction: column;
        gap: 8px;
    }

    .changelog-entry__items li {
        position: relative;
        padding-left: 18px;
        font-size: 14px;
        line-height: 20px;
        color: var(--color-silver);
    }

    .changelog-entry__items li::before {
        content: '';
        position: absolute;
        top: 7px;
        left: 0;
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: var(--color-bluish-grey);
    }

    .changelog-entry--light .changelog-entry__date-version {
        color: var(--color-cool-grey);
    }

    .changelog-entry--light .changelog-entry__date {
        color: var(--color-slate);
    }

    .changelog-entry--light .changelog-entry__type {
        color: var(--color-slate);
        border-color: var(--color-bluish-grey);
    }

    .changelog-entry--light .changelog-entry__card {
        background: var(--color-white);
        border-bottom: none;
        border-radius: 0;
    }

    .changelog-entry--light .changelog-entry__title,
    .changelog-entry--light .changelog-entry__description,
    .changelog-entry--light .changelog-entry__items li {
        color: var(--color-slate);
    }

    .changelog-entry--light .changelog-entry__items li::before {
        background: var(--color-bluish-grey);
    }

    @media (max-width: 991px) {
        .changelog-entry--first .changelog-entry__axis::before {
            top: 12px;
        }

        .changelog-entry__dot {
            top: 12px;
        }

        .changelog-entry__body {
            flex-direction: column;
            gap: 8px;
        }

        .changelog-entry__meta {
            flex-direction: row;
            align-items: center;
            width: 100%;
            padding: 2px 0;
            gap: 8px;
        }

        .changelog-entry__date-version {
            flex: 1;
            min-width: 0;
        }

        .changelog-entry__date,
        .changelog-entry__version {
            display: inline;
        }

        .changelog-entry__date {
            margin-right: 8px;
        }

        .changelog-entry__card {
            padding: 16px;
            border-radius: 4px;
            border-bottom: 1px solid var(--color-charcoal-grey);
        }

        .changelog-entry--first .changelog-entry__card {
            border-radius: 4px;
        }

        .changelog-entry__title {
            font-size: 16px;
        }

        .changelog-entry--light .changelog-entry__card {
            border-radius: 4px;
        }
    }
</style>
